import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { DeckFormatEnum } from "@sorcery/db";
import { DeckVisibilityEnum } from "@sorcery/db";
import {
  Button,
  Checkbox,
  Input,
  Line,
  P,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@sorcery/ui";

import { ModalTypeEnum, useModal } from "~/providers/ModalProvider";
import { deckJotai } from "~/state/deckJotai";
import { deckFormatOptions } from "~/utils/filters";
import { api } from "~/utils/trpc";

export const Settings = () => {
  const trpcUtils = api.useUtils();
  // biome-ignore lint/style/noNonNullAssertion: <explanation>
  const deck = useAtomValue(deckJotai)!;
  const { openModal } = useModal();

  const { mutateAsync: updateSettings, error: trpcError } = api.deck.updateSettings.useMutation({
    onSuccess: () => {
      toast("Deck updated");
      void trpcUtils.deck.getById.invalidate({ id: deck.id });
    },
  });

  const [deckName, setDeckName] = useState(deck.name);
  const [nameMissingError, setNameMissingError] = useState(false);
  const [format, setFormat] = useState(deck.format);
  const [visibility, setVisibility] = useState(deck.visibility);

  const [error, setError] = useState("");

  // Track trpc errors for display
  useEffect(() => {
    if (trpcError) {
      if (trpcError.shape?.data.code === "BAD_REQUEST") {
        setError(trpcError.shape?.message ?? "Something went wrong.");
      } else {
        // don't expose server side error messages
        setError("Something went wrong.");
      }
    }
  }, [trpcError]);

  const onSubmit = () => {
    // clear errs
    setError("");
    setNameMissingError(false);

    if (!deckName) return setNameMissingError(true);

    void updateSettings({
      deckId: deck.id,
      name: deckName,
      format: format,
      visibility: visibility,
    });
  };

  return (
    <div className="mb-8 max-w-sm animate-in fade-in">
      {/* Name */}
      <div className="mb-4 mt-2">
        <div className="mb-1 ">Name</div>
        <Input
          onChange={setDeckName}
          error={nameMissingError}
          value={deckName}
          placeholder={"Name your deck..."}
        />
      </div>

      {/* Format */}

      <div className="mb-4">
        <div className="mb-1">Format</div>
        <Select onValueChange={(v) => setFormat(v as DeckFormatEnum)}>
          <SelectTrigger arrow>
            <SelectValue
              placeholder={
                // biome-ignore lint/style/noNonNullAssertion: <explanation>
                deckFormatOptions.find(
                  (d) => (d.value as DeckFormatEnum) === DeckFormatEnum.Constructed,
                )!.label
              }
            />
          </SelectTrigger>
          <SelectContent>
            {deckFormatOptions.map((d) => (
              <SelectItem key={d.label} value={d.value}>
                {d.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Visibility */}
      <div className="mb-4">
        <div className="mb-1 ">Visibility</div>
        <div className="flex space-x-2 pt-2">
          {Object.values(DeckVisibilityEnum).map((v) => (
            <div
              className="items-top flex space-x-2"
              key={v}
              onClick={(newV) => newV && setVisibility(v)}
            >
              <Checkbox id="visibility" checked={visibility === v} label={v} />
            </div>
          ))}
        </div>

        <P className="mt-2">
          Private decks won't appear on our homepage, in public deck searches, or on your public
          profile. You can still share private decks to your friends by sending them a link, and
          import private decks to TTS.
        </P>
      </div>

      {error && (
        <div className="text-red my-4" role="alert">
          <p>
            <span className="font-bold">Error. </span>
            {error}
            <span>.</span>
          </p>
        </div>
      )}

      <Line className="my-4" />

      <Button onClick={onSubmit}>Save</Button>
      <Line className="my-4" />
      <div className="mb-4">Want to permanently delete your deck? This cannot be undone</div>
      <Button
        variant="destructive"
        onClick={() => {
          openModal(ModalTypeEnum.DELETEDECK, { deckId: deck.id });
        }}
      >
        Delete
      </Button>
    </div>
  );
};
